import * as React from 'react';
import cx from 'classnames';

import genericAddEventLog from 'src/common/utils/genericAddEventLog';
import { LoadSpinner } from 'src/widgets/LoadSpinner';
import { ErrorBoundary } from 'src/utils';
import { useIsServer } from 'src/utils/hooks';

import { FilterBox, ISelectFilterOptions } from './Filters/FilterBox';
import SummaryView from './SummaryView/SummaryView';
import BreakdownView from './BreakdownView/BreakdownView';
import PostsView from './PostsView/PostsView';
import { Notice } from 'src/widgets/Notice';

import { AnalyzeProvider, AnalyzeContext, TAnalyzePage } from './AnalyzeContext';

import styles from './AnalyzePage.scss';

const { useCallback, useState } = React;
export interface IAnalyzePageProps {
  apiEndpoint: string;
  isQa: boolean;
  className?: string;
  organizationId?: string;
  clientId?: string;
  aspirexFilterOptions?: ISelectFilterOptions[];
  aspirexAnalytics?: SegmentAnalytics.AnalyticsJS;
  headerStickyStyles?: {
    top?: number;
    right?: number;
    left?: number;
    bottom?: number;
  };
  selectedBrandId?: number;
}

const Page = ({ page }: { page: TAnalyzePage }) => {
  switch(page) {
    case 'summary':
      return <SummaryView />;
    case 'breakdown':
      return <BreakdownView />;  
    case 'posts':
      return <PostsView />;
    case 'no data': 
      return (
        <Notice type="disabled" className={styles.noDataNotice}>
          There are no campaigns to report on.
          Once creators start completing collaborations, you will see the posts here.
        </Notice>
      );
  }
};

/**
 * @type {React.FunctionComponent}
 */
export const AnalyzePage: React.FunctionComponent<IAnalyzePageProps> = (props) => {
  const {
    aspirexFilterOptions,
    apiEndpoint,
    organizationId,
    clientId,
    isQa,
    aspirexAnalytics,
    headerStickyStyles,
    selectedBrandId,
  } = props;
  const isServer = useIsServer();
  const [page, setPage] = useState<TAnalyzePage>('summary');
  const changePage = useCallback((currentPage: TAnalyzePage) => {
    const previousPage = page;
    setPage(currentPage);
    genericAddEventLog(
      'analyze_change_page',
      {
        previous_page: previousPage,
        current_page: currentPage,
      },
      aspirexAnalytics
    );
  }, [aspirexAnalytics, page]);
  
  if (isServer) {
    return <LoadSpinner centered={true} />;
  }
  return (
    <div className={cx(styles.AnalyzePage, props.className)}>
      <ErrorBoundary>
        <AnalyzeProvider
          apiEndpoint={apiEndpoint}
          organizationId={organizationId}
          clientId={clientId}
          currentPage={page}
          isQa={isQa}
          isAspirex={!!props.aspirexFilterOptions}
          aspirexAnalytics={aspirexAnalytics}
          selectedBrandId={selectedBrandId}
        >
          <AnalyzeContext.Consumer>
            {() => {
              return (
                <>
                  <div
                    className={cx(styles.headerWrapper, { [styles.sticky]: !!headerStickyStyles })}
                    style={headerStickyStyles}
                  >
                    <FilterBox 
                      aspirexFilterOptions={aspirexFilterOptions} 
                      onNoData={setPage}
                    />
                    <div className={styles.header}>
                      <div className={styles.navbar}>
                        <div
                          className={cx(styles.navItem, {
                            [styles.active]: page === 'summary',
                          })}
                          onClick={() => changePage('summary')}
                        >
                          Summary
                        </div>
                        <div
                          className={cx(styles.navItem, {
                            [styles.active]: page === 'breakdown',
                          })}
                          onClick={() => changePage('breakdown')}
                        >
                          Breakdown
                        </div>
                        <div
                          className={cx(styles.navItem, {
                            [styles.active]: page === 'posts',
                          })}
                          onClick={() => changePage('posts')}
                        >
                          Posts
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.children}>
                    <Page page={page} />
                  </div>
                </>
              );
            }}
          </AnalyzeContext.Consumer>
        </AnalyzeProvider>
      </ErrorBoundary>
    </div>
  );
};
